import React from "react";
import { Column } from "../components/Layout";
import { Signature } from "../components/Signature";
import { FadeIn } from "../components/FadeIn";
import { Seo } from "../components/Seo";
import { AuthorSignature } from "../components/AuthorSignature";

const ABOUT_IMAGE =
  "https://customer-assets.emergentagent.com/job_quill-study/artifacts/45bemeno_image.png";

const About = () => {
  return (
    <div data-testid="about-page">
      <Seo
        title="Zukiswa Phoofolo, Writer-Anthropologist · The Raven's Quill"
        description="Zukiswa Phoofolo studies a single life the way others study a culture: from the inside, over time, and in the company of the people who shaped it."
        path="/the-anthropologist"
      />
      <section className="pt-20 pb-16 md:pt-28 md:pb-20">
        <Column>
          <Signature label="The Anthropologist" testid="about-signature" />
          <FadeIn>
            <h1 className="text-[32px] md:text-[38px] italic leading-[1.25] text-[#1C1A17]" data-testid="about-headline">
              The Anthropologist
            </h1>
          </FadeIn>
        </Column>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="mx-auto max-w-[820px] px-6 md:px-8">
          <FadeIn data-testid="about-portrait" className="mb-8 lg:mb-2 lg:ml-10 lg:float-right lg:w-[300px]">
            <img
              src={ABOUT_IMAGE}
              alt="Zukiswa Phoofolo, writer-anthropologist"
              className="w-full h-auto mx-auto max-w-[320px] lg:max-w-none border border-[#7A2E2E] p-2 sm:p-3 grayscale"
              loading="lazy"
            />
          </FadeIn>
          <div className="space-y-7 text-[17px] md:text-[18px] leading-[1.8] text-[#1C1A17]">
            <FadeIn as="p">
              The Raven&rsquo;s Quill is the work of Zukiswa Phoofolo, a
              writer-anthropologist who studies a single life the way others study a
              culture: from the inside, over time, and in the company of the people who
              shaped it.
            </FadeIn>
            <FadeIn as="p">
              Her conviction is simple. A life of consequence deserves more than a
              flattering memoir assembled over a few interviews. It deserves to be
              understood. Trained in the methods of ethnographic fieldwork, she brings
              to each commission the patience of the discipline: deep archival reading,
              a residency within the subject&rsquo;s world, conversations across the
              circle that surrounds them, and the careful reading of photographs as
              evidence rather than decoration. What results is not a ghostwritten record
              but a work of anthropological portraiture, authored with the subject
              rather than for them, and bound as an heirloom intended to outlive
              everyone who first holds it.
            </FadeIn>
            <FadeIn as="p">
              One question has run through all of her work: how a life gathers meaning,
              and how that meaning is carried forward across generations. She first
              pursued it at the beginning of the arc, across seventeen years in early
              childhood development throughout the Global South, in sub-Saharan Africa
              and across several countries of Asia, attending to how people are formed
              and how a culture hands itself to the next. She pursues it now at the
              other end, preserving lives in their fullness, when their shape is complete
              and ready to be understood.
            </FadeIn>
            <FadeIn as="p">
              Her current anthropological research sits exactly between the two. It
              introduces a single object, her late grandmother&rsquo;s vintage leather
              suitcase, a thing that carries what a life accumulated and holds no
              curricular purpose, into a community early childhood centre in Gauteng, and
              studies what the encounter produces: how an object of the old is enacted,
              again and again, in each entanglement it enters among the new. Her
              grandmother&rsquo;s name was Zukiswa too.
            </FadeIn>
            <FadeIn as="p">
              That study is also why The Raven&rsquo;s Quill makes books, and not files.
              In a world where the digital is the default, the choice of a physical
              object is a deliberate one. A file is weightless and endlessly copied; an
              heirloom must be carried, opened, and handed on by hand. The materiality is
              the meaning. A suitcase, or a bound book, is chosen precisely because it
              has weight, because a life that mattered should be held.
            </FadeIn>
            <FadeIn as="p">
              She was named among the Mail and Guardian&rsquo;s 200 Young South Africans
              of 2018, and continues her formal study of anthropology while building The
              Raven&rsquo;s Quill into a fine press devoted to the preservation of
              extraordinary lives. She works from Pretoria, and undertakes only a few
              Life Studies at a time.
            </FadeIn>
            <FadeIn>
              <AuthorSignature className="pt-2" scale={0.8} />
            </FadeIn>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
